import React, { useState } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";

export default function Login() {
  const { t } = useI18n();
  usePageMeta({ title: t("login_title"), noindex: true });
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const redirectTo = location.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        toast.error(error.message || t("auth_error"));
        return;
      }
      nav(redirectTo, { replace: true });
    } catch {
      toast.error(t("auth_error"));
    } finally {
      setLoading(false);
    }
  };

  const loginGoogle = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/auth-callback` },
      });
      if (error) toast.error(error.message || t("auth_error"));
    } catch {
      toast.error(t("auth_error"));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto py-16 px-4 sm:px-0 animate-in fade-in duration-500">
      <h1 className="text-2xl font-serif-en mb-6 text-center">{t("login_title")}</h1>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="text-sm text-muted-foreground">{t("login_email")}</label>
          <Input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
            autoComplete="email" data-testid="login-email-input" />
        </div>
        <div>
          <label htmlFor="login-password" className="text-sm text-muted-foreground">{t("login_password")}</label>
          <Input id="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required
            autoComplete="current-password" data-testid="login-password-input" />
        </div>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-primary underline underline-offset-4" data-testid="login-forgot-link">
            {t("login_forgot")}
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
          {loading ? "…" : t("login_signin")}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground uppercase">{t("login_or")}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <Button variant="outline" className="w-full" onClick={loginGoogle} disabled={googleLoading} data-testid="login-google-btn">
        {googleLoading ? "…" : t("login_google")}
      </Button>

      <p className="text-sm text-center text-muted-foreground mt-6">
        {t("login_no_account")}{" "}
        <Link to="/signup" className="text-primary underline underline-offset-4" data-testid="login-signup-link">{t("login_signup_link")}</Link>
      </p>
    </div>
  );
}
